import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  FileText, Search, Plus, Save, Trash2, X, Edit2, Eye, EyeOff,
  RefreshCw, AlertCircle, CheckCircle, Globe, Image, Code, Link2, Type, ToggleRight
} from 'lucide-react';
import MediaPicker from '../components/MediaPicker';

interface CMSEntry {
  id: number;
  key: string;
  type: 'text' | 'html' | 'image' | 'url' | 'json' | 'boolean';
  value: string;
  label: string;
  description?: string;
  category: string;
  isActive: number;
  updatedAt: number;
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  text: Type,
  html: Code,
  image: Image,
  url: Link2,
  json: Code,
  boolean: ToggleRight,
};

const TYPE_LABELS: Record<string, string> = {
  text: '文字',
  html: 'HTML',
  image: '圖片 URL',
  url: '連結',
  json: 'JSON',
  boolean: '布林',
};

const DEFAULT_CATEGORIES = ['home', 'store', 'story', 'media', 'site'];

const CATEGORY_LABELS: Record<string, string> = {
  home: '首頁',
  story: '品牌故事',
  media: '媒體報道',
  site: '網站設定',
};

const AdminCMS: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<CMSEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('全部');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CMSEntry | null>(null);
  const [form, setForm] = useState<Partial<CMSEntry>>({
    key: '',
    type: 'text',
    value: '',
    label: '',
    description: '',
    category: 'general',
    isActive: 1,
  });
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/cms');
      if (!res.ok) throw new Error('載入 CMS 失敗');
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchEntries();
  }, [navigate]);

  const categories = useMemo(() => {
    const allowed = new Set(DEFAULT_CATEGORIES);
    const set = new Set(entries.filter((e) => allowed.has(e.category)).map((e) => e.category));
    DEFAULT_CATEGORIES.forEach((c) => set.add(c));
    return Array.from(set).sort();
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const term = search.toLowerCase();
    return entries.filter((e) => {
      const matchesSearch =
        e.key.toLowerCase().includes(term) ||
        e.label.toLowerCase().includes(term) ||
        (e.description || '').toLowerCase().includes(term);
      const allowed = new Set(DEFAULT_CATEGORIES);
      const matchesCategory = activeCategory === '全部' ? allowed.has(e.category) : e.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [entries, search, activeCategory]);

  const groupedEntries = useMemo(() => {
    const groups: Record<string, CMSEntry[]> = {};
    for (const e of filteredEntries) {
      if (!groups[e.category]) groups[e.category] = [];
      groups[e.category].push(e);
    }
    return groups;
  }, [filteredEntries]);

  const openCreate = () => {
    setEditingEntry(null);
    setForm({
      key: '',
      type: 'text',
      value: '',
      label: '',
      description: '',
      category: activeCategory === '全部' ? 'home' : activeCategory,
      isActive: 1,
    });
    setModalOpen(true);
  };

  const openEdit = (entry: CMSEntry) => {
    setEditingEntry(entry);
    setForm({ ...entry });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingEntry(null);
  };

  const showToast = (msg: string, isError = false) => {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(null), 4000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.key || !form.label) {
      showToast('請填寫 Key 與標籤', true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        key: form.key?.trim(),
        type: form.type,
        value: form.value ?? '',
        label: form.label?.trim(),
        description: form.description?.trim() || undefined,
        category: form.category?.trim() || 'general',
        isActive: form.isActive,
      };

      let res;
      if (editingEntry) {
        res = await apiFetch(`/api/public/admin/cms/${encodeURIComponent(editingEntry.key)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await apiFetch('/api/public/admin/cms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');

      showToast(editingEntry ? '已更新' : '已建立');
      closeModal();
      fetchEntries();
    } catch (err: any) {
      showToast(err.message || '儲存失敗', true);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry: CMSEntry) => {
    if (!confirm(`確定要刪除「${entry.label}」(${entry.key}) 嗎？`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/cms/${encodeURIComponent(entry.key)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '刪除失敗');
      showToast('已刪除');
      fetchEntries();
    } catch (err: any) {
      showToast(err.message || '刪除失敗', true);
    }
  };

  const handleToggleActive = async (entry: CMSEntry) => {
    try {
      const res = await apiFetch(`/api/public/admin/cms/${encodeURIComponent(entry.key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !entry.isActive }),
      });
      if (!res.ok) throw new Error('更新失敗');
      fetchEntries();
    } catch (err: any) {
      showToast(err.message || '更新失敗', true);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const renderValuePreview = (entry: CMSEntry) => {
    if (entry.type === 'boolean') {
      return entry.value === '1' || entry.value === 'true' ? '是' : '否';
    }
    if (entry.type === 'html') {
      return <span className="text-xs text-gray-500" dangerouslySetInnerHTML={{ __html: entry.value }} />;
    }
    if (entry.type === 'image' || entry.type === 'url') {
      return (
        <a href={entry.value} target="_blank" rel="noopener noreferrer" className="text-xs text-red-600 hover:underline truncate max-w-xs inline-block">
          {entry.value}
        </a>
      );
    }
    return <span className="truncate max-w-xs inline-block">{entry.value}</span>;
  };

  const inputTypeFor = (cmsType: string) => {
    if (cmsType === 'boolean') return 'checkbox';
    if (cmsType === 'html' || cmsType === 'json') return 'textarea';
    return 'text';
  };

  return (
    <AdminLayout currentPage="cms" onLogout={handleLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Globe className="w-6 h-6 text-red-600" />
          網頁內容管理 (CMS)
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          管理網站前台文字、圖片、HTML 等內容，修改後會即時反映到網站。
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Key、標籤、說明..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">分類</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory('全部')}
                className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                  activeCategory === '全部'
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                全部
              </button>
              {categories.filter((c) => c !== '全部').map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                    activeCategory === c
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {CATEGORY_LABELS[c] || c}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchEntries}
              className="flex items-center gap-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
              title="重新整理"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={openCreate}
              className="flex items-center gap-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
            >
              <Plus size={16} /> 新增內容
            </button>
          </div>
        </div>
      </div>

      {/* Entries */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
        ) : Object.keys(groupedEntries).length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            沒有找到 CMS 內容
          </div>
        ) : (
          Object.entries(groupedEntries).map(([category, items]) => (
            <div key={category} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 font-semibold text-sm text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                {CATEGORY_LABELS[category] || category}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50/50 dark:bg-gray-900/50 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    <tr>
                      <th className="p-4 w-10">狀態</th>
                      <th className="p-4">Key</th>
                      <th className="p-4">標籤</th>
                      <th className="p-4">類型</th>
                      <th className="p-4">內容預覽</th>
                      <th className="p-4">說明</th>
                      <th className="p-4 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map((entry) => {
                      const TypeIcon = TYPE_ICONS[entry.type] || FileText;
                      return (
                        <tr key={entry.key} className="hover:bg-red-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="p-4">
                            <button
                              onClick={() => handleToggleActive(entry)}
                              className={`${entry.isActive ? 'text-green-600' : 'text-gray-400'} hover:opacity-70`}
                              title={entry.isActive ? '啟用中，點擊停用' : '已停用，點擊啟用'}
                            >
                              {entry.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                          </td>
                          <td className="p-4 font-mono text-xs text-gray-600 dark:text-gray-400">{entry.key}</td>
                          <td className="p-4 font-medium text-gray-800 dark:text-white">{entry.label}</td>
                          <td className="p-4">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                              <TypeIcon size={12} /> {TYPE_LABELS[entry.type] || entry.type}
                            </span>
                          </td>
                          <td className="p-4 text-gray-600 dark:text-gray-300 max-w-xs">
                            {renderValuePreview(entry)}
                          </td>
                          <td className="p-4 text-gray-500 dark:text-gray-400 text-xs max-w-xs">{entry.description || '-'}</td>
                          <td className="p-4 text-right whitespace-nowrap">
                            <button
                              onClick={() => openEdit(entry)}
                              className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700 transition-colors mr-2"
                            >
                              <Edit2 size={12} /> 編輯
                            </button>
                            <button
                              onClick={() => handleDelete(entry)}
                              className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1.5 rounded text-xs hover:bg-red-700 transition-colors"
                            >
                              <Trash2 size={12} /> 刪除
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Media Picker */}
      {showMediaPicker && (
        <MediaPicker
          selectedUrl={form.value}
          onSelect={(url) => { setForm({ ...form, value: url }); setShowMediaPicker(false); }}
          onClose={() => setShowMediaPicker(false)}
        />
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSave} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingEntry ? '編輯內容' : '新增內容'}
                </h2>
                <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <X size={22} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key *</label>
                  <input
                    type="text"
                    required
                    disabled={!!editingEntry}
                    value={form.key || ''}
                    onChange={(e) => setForm({ ...form, key: e.target.value })}
                    placeholder="例如 home.hero.title"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">建立後不可修改，請使用英文與點號命名。</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類型 *</label>
                    <select
                      value={form.type || 'text'}
                      onChange={(e) => setForm({ ...form, type: e.target.value as CMSEntry['type'] })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    >
                      {Object.entries(TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分類 *</label>
                    <select
                      required
                      value={form.category || ''}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    >
                      {DEFAULT_CATEGORIES.map((c) => (
                        <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">標籤 *</label>
                  <input
                    type="text"
                    required
                    value={form.label || ''}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">內容 *</label>
                  {form.type === 'html' || form.type === 'json' ? (
                    <textarea
                      required
                      rows={8}
                      value={form.value || ''}
                      onChange={(e) => setForm({ ...form, value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white font-mono text-sm"
                    />
                  ) : form.type === 'boolean' ? (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={form.value === '1' || form.value === 'true'}
                        onChange={(e) => setForm({ ...form, value: e.target.checked ? '1' : '0' })}
                        className="w-4 h-4 text-red-600 rounded"
                      />
                      啟用 / 是
                    </label>
                  ) : form.type === 'image' ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          required
                          value={form.value || ''}
                          onChange={(e) => setForm({ ...form, value: e.target.value })}
                          placeholder="https://..."
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => setShowMediaPicker(true)}
                          className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-1"
                        >
                          <Image size={16} /> 從媒體庫挑選
                        </button>
                      </div>
                      {form.value && (
                        <div className="relative w-40 h-40 border rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                          <img src={form.value} alt="預覽" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      required
                      value={form.value || ''}
                      onChange={(e) => setForm({ ...form, value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">說明</label>
                  <input
                    type="text"
                    value={form.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.isActive === 1}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">啟用（前台才會顯示）</span>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                  {saving ? '儲存中...' : '儲存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCMS;
